import * as React from 'react';
import * as styled from './styled';
import {Field, FieldProps, useFormikContext} from 'formik';
import {ProgramObject, TrainingItem} from 'api/program/types';
import {FileType, getFilePath, getImageByPreview} from 'api/library';
import InputContainer from "containers/InputContainer";
import Row from 'components/Row';
import Column from 'components/Column';
import Input, {InputVariant} from 'components/Input';
import Textarea from 'components/Textarea';
import Upload, {UploadVariant} from 'components/Upload';
import ControlButton, {ControlButtonVariant} from 'components/ControlButton';
import VideoPlayer from 'components/VideoPlayer';
import {TagBuilder} from 'components/TagBuilder';
import {IconTrash} from 'icons';

/**
 * The settings of a the training.
 * @return {React.ReactElement}
 * @constructor
 */
export default function Settings(): React.ReactElement {
  const {
    values, setFieldValue,
  } = useFormikContext<Partial<ProgramObject<TrainingItem>>>();

  /**
   * Render header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    /**
     * Render preview.
     * @return {JSX.Element}
     */
    function renderPreview(): JSX.Element {
      return (
        <Column>
          <Field name="preview">
            {({field}: FieldProps) => (
              <React.Fragment>
                {field.value ? (
                  <styled.PickedWrapper>
                    <styled.ControlWrapper>
                      <ControlButton
                        type="button"
                        variant={ControlButtonVariant.Accent}
                        onClick={() => setFieldValue('preview', undefined)}
                      >
                        <IconTrash />
                      </ControlButton>
                    </styled.ControlWrapper>
                    <styled.Preview
                      src={getImageByPreview(values.preview)}
                      alt="Изображение программы"
                    />
                  </styled.PickedWrapper>
                ) : (
                  <styled.UploadWrapper>
                    <Upload
                      type={FileType.Image}
                      variant={UploadVariant.Accent}
                      tabbarProps={{
                        param: 'inner',
                      }}
                      picker={{
                        onSelect: (attached) => (
                          setFieldValue('preview', {
                            id: attached[0].id,
                            type: attached[0].type,
                            url: attached[0].url,
                          })
                        )
                      }}
                    />
                  </styled.UploadWrapper>
                )}
              </React.Fragment>
            )}
          </Field>
        </Column>
      );
    }

    /**
     * Render video.
     * @return {JSX.Element}
     */
    function renderVideo(): JSX.Element {
      return (
        <Column>
          <Field name="days[0].video">
            {({field}: FieldProps) => (
              field.value ? (
                <styled.PickedWrapper>
                  <styled.ControlWrapper>
                    <ControlButton
                      type="button"
                      variant={ControlButtonVariant.Accent}
                      onClick={() => setFieldValue('days[0].video', undefined)}
                    >
                      <IconTrash />
                    </ControlButton>
                  </styled.ControlWrapper>
                  <VideoPlayer
                    src={values.days ? values.days[0].video ? getFilePath(values.days[0].video.id) : '' : ''}
                    fill
                  />
                </styled.PickedWrapper>
              ) : (
                <styled.UploadWrapper>
                  <Upload
                    type={FileType.Video}
                    variant={UploadVariant.Accent}
                    tabbarProps={{
                      param: 'inner',
                    }}
                    picker={{
                      onSelect: (attached) => (
                        setFieldValue('days[0].video', {
                          id: attached[0].id,
                          type: attached[0].type,
                          url: attached[0].url,
                        })
                      )
                    }}
                  />
                </styled.UploadWrapper>
              )
            )}
          </Field>
        </Column>
      );
    }

    return (
      <Row>
        {renderPreview()}
        {renderVideo()}
      </Row>
    );
  }

  /**
   * Render tags.
   * @return {JSX.Element}
   */
  function renderTags(): JSX.Element {
    return (
      <Row>
        <Column>
          <InputContainer label="Теги">
            <TagBuilder
              type="programs"
              tags={values.tags}
              inputProps={{
                variant: InputVariant.Default,
              }}
            />
          </InputContainer>
        </Column>
      </Row>
    );
  }

  /**
   * Render title.
   * @return {JSX.Element}
   */
  function renderTitle(): JSX.Element {
    return (
      <Row>
        <Column>
          <Field name="title">
            {({field}: FieldProps) => (
              <Input
                {...field}
                label="Название программы тренировок"
                placeholder="Введите название программы"
                required
              />
            )}
          </Field>
        </Column>
      </Row>
    );
  }

  /**
   * Render description.
   * @return {JSX.Element}
   */
  function renderDescription(): JSX.Element {
    return (
      <Row>
        <Column>
          <Field name="description">
            {({field}: FieldProps) => (
              <Textarea
                {...field}
                label="Описание"
                placeholder="Введите описание"
                rows={3}
                required
              />
            )}
          </Field>
        </Column>
      </Row>
    );
  }

  return (
    <styled.Wrapper>
      {renderHeader()}
      <br/>
      {renderTags()}
      {renderTitle()}
      {renderDescription()}
    </styled.Wrapper>
  );
}
